import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import {
  ChangeEvent,
  FC,
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { State } from 'redux/initialState';
import { SocietyAiPlanTier } from 'types/Society';
import { setModal } from 'modules/_shared/redux/modalActions';
import { useGetActualSociety } from 'hooks/useGetActualSociety';
import { useGetMenu } from 'modules/administratives/hooks/useGetMenu';
import cleanText from 'utils/cleanText';

import {
  appendAiMessage,
  clearAiConversation,
  getAiConversation,
  getAiConversations,
  getAiUsage,
} from 'modules/ai/redux/aiActions';
import { useAiChatStream } from 'modules/ai/hooks/useAiChatStream';
import AiQuotaExceededModal from 'modules/ai/modals/AiQuotaExceededModal';
import { AiQuotaExceededModalProvider } from 'modules/ai/modals/AiQuotaExceededModal/context/AiQuotaExceededModalContext';

import { SocietyAiAgentPageContextType } from '../types/SocietyAiAgentPageContextType';
import { SocietyAiAgentPageInitValue } from '../constants/societyAiAgentPageInitValue';
import { ApiProvider } from '../types/ApiProvider';
import { Message } from '../types/Message';
import { createMessageId } from '../utils/createMessageId';

const SocietyAiAgentPageContext = createContext<SocietyAiAgentPageContextType>(
  SocietyAiAgentPageInitValue
);

export const SocietyAiAgentPageProvider: FC<{
  children: ReactNode;
}> = ({ children }) => {
  const { societyId } = useParams<{ societyId: string }>();
  const dispatch = useDispatch();

  const [inputText, setInputText] = useState<string>('');
  const [selectedApi, setSelectedApi] = useState<ApiProvider>('anthropic');
  const [conversationId, setConversationId] = useState<string | null>(null);

  const aiMessages = useSelector(
    (state: State) => state.ai?.currentConversation?.messages ?? []
  );
  const aiConversations = useSelector(
    (state: State) => state.ai?.conversations ?? []
  );
  const activeConversationId = useSelector(
    (state: State) => state.ai?.currentConversation?.id ?? null
  );
  const usage = useSelector((state: State) => state.ai?.usage);

  const { sendMessage, isStreaming, streamingText, error } = useAiChatStream();

  useGetActualSociety(societyId);
  useGetMenu(null);

  useEffect(() => {
    if (!societyId) return;
    dispatch(clearAiConversation() as any);
    dispatch(getAiUsage(societyId) as any);
    dispatch(getAiConversations(societyId) as any);
  }, [dispatch, societyId]);

  useEffect(() => {
    if (error === 'AI_QUOTA_EXCEEDED' && usage) {
      dispatch(
        setModal(
          <AiQuotaExceededModalProvider
            used={usage.used || 0}
            limit={usage.limit || 0}
            tier={usage.tier || SocietyAiPlanTier.FREE}
          >
            <AiQuotaExceededModal />
          </AiQuotaExceededModalProvider>
        )
      );
    }
  }, [dispatch, error, usage]);

  const messages: Message[] = useMemo(
    () =>
      aiMessages.map((m) => ({
        id: m.id,
        role: m.role,
        content: m.content,
        createdAt: m.createdAt ? new Date(m.createdAt) : new Date(),
      })),
    [aiMessages]
  );

  const handleChangeInputText = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setInputText(cleanText(event.target.value));
  };

  const handleChangeSelectedApi = (api: ApiProvider) => {
    setSelectedApi(api);
  };

  const handleResetConversation = () => {
    dispatch(clearAiConversation() as any);
    if (societyId) dispatch(getAiConversations(societyId) as any);
    setConversationId(null);
    setInputText('');
  };

  const handleSelectConversation = (id: string) => {
    dispatch(getAiConversation(id) as any);
    setConversationId(id);
    setInputText('');
  };

  const handleSendMessage = async () => {
    const trimmed = inputText.trim();
    if (!trimmed || !societyId || isStreaming) return;

    dispatch(
      appendAiMessage({
        id: createMessageId('u'),
        role: 'user',
        content: trimmed,
        createdAt: new Date().toISOString(),
      })
    );
    setInputText('');

    try {
      await sendMessage({
        societyId,
        conversationId,
        message: trimmed,
        onConversationId: (id) => setConversationId(id),
        onDone: () => {
          if (societyId) dispatch(getAiConversations(societyId) as any);
        },
      });
    } catch {
      // quota exceeded handled via error effect above
    }
  };

  return (
    <SocietyAiAgentPageContext.Provider
      value={{
        data: {
          inputText,
          selectedApi,
          apiOptions: [],
          messages,
          isThinking: isStreaming,
          streamingText,
          conversations: aiConversations,
          activeConversationId,
        },
        actions: {
          handleChangeInputText,
          handleResetConversation,
          handleSelectConversation,
          handleChangeSelectedApi,
          handleSendMessage,
        },
      }}
    >
      {children}
    </SocietyAiAgentPageContext.Provider>
  );
};

export const useSocietyAiAgentPage = () => {
  const context = useContext(SocietyAiAgentPageContext);
  if (!context) {
    throw new Error(
      'useSocietyAiAgentPage must be used within a SocietyAiAgentPageProvider'
    );
  }
  return context;
};
